import { useEffect, useMemo, useState, useCallback } from 'react';
import { Layout } from '../components/layout/Layout';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '../components/ui/table';
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger,
} from '../components/ui/dropdown-menu';
import {
  Plus, Search, Eye, Edit, Building2, Phone, Mail, MoreHorizontal, Power, Users,
} from 'lucide-react';
import api, { formatApiError } from '../lib/api';
import { CustomerDialog } from '../components/dialogs/CustomerDialog';
import { PortalUsersDialog } from '../components/dialogs/PortalUsersDialog';
import { toast } from 'sonner';
import { useAuth } from '../contexts/AuthContext';

export const Customers = () => {
  const { user } = useAuth();
  const canEdit = ['admin', 'accounts'].includes(user?.role);

  const [customers, setCustomers] = useState([]);
  const [services, setServices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [portalCustomer, setPortalCustomer] = useState(null);
  const [showInactive, setShowInactive] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [cRes, sRes] = await Promise.all([
        api.get('/customers', { params: { include_inactive: showInactive } }),
        api.get('/services'),
      ]);
      setCustomers(cRes.data || []);
      setServices(sRes.data || []);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || 'Failed to load customers');
    } finally {
      setLoading(false);
    }
  }, [showInactive]);

  useEffect(() => { load(); }, [load]);

  const filtered = useMemo(() => {
    const q = searchTerm.trim().toLowerCase();
    if (!q) return customers;
    return customers.filter((c) =>
      (c.company_name || '').toLowerCase().includes(q) ||
      (c.code || '').toLowerCase().includes(q) ||
      (c.contact_person || '').toLowerCase().includes(q) ||
      (c.email || '').toLowerCase().includes(q)
    );
  }, [customers, searchTerm]);

  const totals = {
    total: customers.length,
    active: customers.filter((c) => c.active).length,
    withPricing: customers.filter((c) => (c.pricing || []).length > 0).length,
  };

  const openCreate = () => { setEditing(null); setDialogOpen(true); };
  const openEdit = (c) => { setEditing(c); setDialogOpen(true); };

  const toggleActive = async (c) => {
    try {
      await api.patch(`/customers/${c.id}`, { active: !c.active });
      toast.success(c.active ? `${c.company_name} deactivated` : `${c.company_name} reactivated`);
      load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    }
  };

  return (
    <Layout title="Customers" subtitle="Client Management & Billing Rules">
      <div className="space-y-6 animate-fade-in" data-testid="customers-page">

        {/* Stats Row */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <StatCard label="Total Customers" value={totals.total} icon={Building2} accent="#0D5B69" />
          <StatCard label="Active" value={totals.active} accent="#80C030" />
          <StatCard label="With Custom Pricing" value={totals.withPricing} accent="#0D5B69" />
        </div>

        {/* Table */}
        <Card className="bg-white border-slate-200" data-testid="customers-table-card">
          <CardHeader className="border-b border-slate-100 bg-slate-50/50 py-4">
            <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
              <CardTitle className="font-heading text-lg text-[#0D5B69]">Customer Directory</CardTitle>
              <div className="flex items-center gap-3">
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                  <Input
                    type="text" placeholder="Search customers..."
                    value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)}
                    className="pl-10 w-56" data-testid="customer-search"
                  />
                </div>
                <Button
                  variant="outline" size="sm"
                  onClick={() => setShowInactive((v) => !v)}
                  data-testid="toggle-inactive"
                >
                  {showInactive ? 'Hide Inactive' : 'Show Inactive'}
                </Button>
                {canEdit && (
                  <Button
                    onClick={openCreate}
                    className="bg-[#80C030] hover:bg-[#6FA528] text-white gap-1 font-semibold"
                    data-testid="add-customer-btn"
                  >
                    <Plus className="w-4 h-4" /> Add Customer
                  </Button>
                )}
              </div>
            </div>
          </CardHeader>
          <CardContent className="p-0">
            <Table>
              <TableHeader>
                <TableRow className="bg-slate-50 hover:bg-slate-50">
                  <TableHead className="text-xs font-semibold text-slate-500 uppercase tracking-wider">Customer</TableHead>
                  <TableHead className="text-xs font-semibold text-slate-500 uppercase tracking-wider">Contact</TableHead>
                  <TableHead className="text-xs font-semibold text-slate-500 uppercase tracking-wider">VAT Number</TableHead>
                  <TableHead className="text-xs font-semibold text-slate-500 uppercase tracking-wider">Terms</TableHead>
                  <TableHead className="text-xs font-semibold text-slate-500 uppercase tracking-wider">Pricing</TableHead>
                  <TableHead className="text-xs font-semibold text-slate-500 uppercase tracking-wider">Status</TableHead>
                  <TableHead className="text-xs font-semibold text-slate-500 uppercase tracking-wider w-20">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {loading ? (
                  <TableRow><TableCell colSpan={7} className="text-center py-12 text-slate-400">Loading customers…</TableCell></TableRow>
                ) : filtered.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={7} className="text-center py-12 text-slate-400" data-testid="empty-customers">
                      {searchTerm ? 'No customers match your search.' : 'No customers yet. Add your first customer to get started.'}
                    </TableCell>
                  </TableRow>
                ) : filtered.map((c, idx) => (
                  <TableRow key={c.id} className="hover:bg-slate-50/80" data-testid={`customer-row-${idx}`}>
                    <TableCell>
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 bg-[#0D5B69]/10 rounded-lg flex items-center justify-center">
                          <Building2 className="w-5 h-5 text-[#0D5B69]" />
                        </div>
                        <div>
                          <p className="font-medium text-sm text-slate-900">{c.company_name}</p>
                          <p className="text-xs text-slate-500">{c.code || '—'}</p>
                        </div>
                      </div>
                    </TableCell>
                    <TableCell>
                      {c.contact_person ? <p className="text-sm text-slate-900">{c.contact_person}</p> : null}
                      {c.phone && <p className="text-xs text-slate-500 flex items-center gap-1 mt-0.5"><Phone className="w-3 h-3" /> {c.phone}</p>}
                      {c.email && <p className="text-xs text-slate-500 flex items-center gap-1"><Mail className="w-3 h-3" /> {c.email}</p>}
                    </TableCell>
                    <TableCell className="text-sm text-slate-600 font-mono">{c.vat_number || '—'}</TableCell>
                    <TableCell className="text-sm text-slate-600">{c.payment_terms_days} days</TableCell>
                    <TableCell>
                      {(c.pricing || []).length > 0 ? (
                        <Badge className="bg-[#80C030]/15 text-[#0D5B69] border-[#80C030]/30 hover:bg-[#80C030]/15">
                          {c.pricing.length} custom rate{c.pricing.length === 1 ? '' : 's'}
                        </Badge>
                      ) : (
                        <span className="text-xs text-slate-400">Default rates</span>
                      )}
                    </TableCell>
                    <TableCell>
                      {c.active ? (
                        <Badge className="bg-green-100 text-green-700 border-green-200 hover:bg-green-100">Active</Badge>
                      ) : (
                        <Badge className="bg-slate-100 text-slate-500 border-slate-200 hover:bg-slate-100">Inactive</Badge>
                      )}
                    </TableCell>
                    <TableCell>
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <Button variant="ghost" size="sm" className="h-8 w-8 p-0" data-testid={`customer-actions-${idx}`}>
                            <MoreHorizontal className="w-4 h-4 text-slate-500" />
                          </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end">
                          <DropdownMenuItem className="gap-2" onClick={() => openEdit(c)}>
                            <Eye className="w-4 h-4" /> View / Edit
                          </DropdownMenuItem>
                          {canEdit && (
                            <DropdownMenuItem className="gap-2" onClick={() => openEdit(c)}>
                              <Edit className="w-4 h-4" /> Edit Customer
                            </DropdownMenuItem>
                          )}
                          {canEdit && (
                            <DropdownMenuItem className="gap-2" onClick={() => setPortalCustomer(c)} data-testid={`customer-portal-users-${idx}`}>
                              <Users className="w-4 h-4" /> Portal Users
                            </DropdownMenuItem>
                          )}
                          {canEdit && (
                            <DropdownMenuItem className="gap-2" onClick={() => toggleActive(c)} data-testid={`customer-toggle-${idx}`}>
                              <Power className="w-4 h-4" /> {c.active ? 'Deactivate' : 'Reactivate'}
                            </DropdownMenuItem>
                          )}
                        </DropdownMenuContent>
                      </DropdownMenu>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </CardContent>
        </Card>
      </div>

      <CustomerDialog
        open={dialogOpen}
        onOpenChange={setDialogOpen}
        customer={editing}
        services={services}
        onSaved={load}
      />

      <PortalUsersDialog
        open={!!portalCustomer}
        onOpenChange={(o) => !o && setPortalCustomer(null)}
        customer={portalCustomer}
      />
    </Layout>
  );
};

const StatCard = ({ label, value, icon: Icon, accent = '#0D5B69' }) => (
  <Card className="bg-white border-slate-200">
    <CardContent className="p-4">
      <div className="flex items-center justify-between">
        <div>
          <p className="text-sm text-slate-500">{label}</p>
          <p className="text-2xl font-bold font-heading" style={{ color: accent }}>{value}</p>
        </div>
        {Icon && (
          <div className="w-10 h-10 rounded-lg flex items-center justify-center" style={{ background: `${accent}15` }}>
            <Icon className="w-5 h-5" style={{ color: accent }} />
          </div>
        )}
      </div>
    </CardContent>
  </Card>
);
